import { Form } from '@livecontrol/core-ui';
import { Nix } from '@livecontrol/core-utils';
import { Modal } from '@livecontrol/scheduler/components';
// import { FormikProvider } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useReplaceAsset, useReplaceAssetInChunks } from '../../graphql';
import { FileDrop } from './file-drop';

interface Props {
  clientId: string;
  assetId: number;
  show: boolean;
  togglePlayer: () => void;
  onClose: () => void;
}

interface FileData {
  file: File;
  duration: number;
}

interface Alert {
  message: string;
  variant?: string;
  clear: () => void;
}

const FIVE_GB = 5 * 1024 * 1024 * 1024; // 5 GB en bytes
// const FIVE_GB = 7 * 1024 * 1024;  // test 7 megas

const MAX_CHARACTERS_DISPLAYED = 10;

export const UploadModal = ({ clientId, assetId, show, onClose: onClose_, togglePlayer }: Props): React.ReactElement => {
  const [fileData, setFileData] = useState<FileData | undefined>();
  const [alert, setAlert] = useState<Alert | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingMultipart, setLoadingMultipart] = useState(false);

  const trimmedName = useMemo(() => {
    if (!fileData?.file?.name) {
      return '';
    }

    const parts = fileData.file.name.split('.');
    const extension = parts.pop();
    const name = parts.join('.');

    return `${name.length > MAX_CHARACTERS_DISPLAYED ? `${name.slice(0, MAX_CHARACTERS_DISPLAYED - 3)}...${name.slice(-3)}` : name}${extension ? `.${extension}` : ''} `;
  }, [fileData?.file?.name]);

  const [
    replaceAsset,
    { progress, error, loading: uploading, abort }
  ] = useReplaceAsset();

  const [
    replaceAssetInParts,
    { progress: multipartProgress, error: multipartError, loading: multipartUploading, abort: multipartAbort }
  ] = useReplaceAssetInChunks();

  const onClose = useCallback(() => {
    if (uploading || loading || multipartUploading) {
      return;
    }

    onClose_();
  }, [uploading, loading, multipartUploading, onClose_]);

  const cancel = useCallback(() => {
    abort?.();
    multipartAbort?.();
    onClose_();
  }, [abort, multipartAbort, onClose_]);

  const upload = useCallback(async () => {
    if (!fileData?.file) {
      return;
    }

    try {

      const isMultipartUpload = fileData.file.size > FIVE_GB;
      await (isMultipartUpload ? replaceAssetInParts({ assetId, clientId: Number(clientId), file: fileData.file, duration:fileData.duration}) : replaceAsset({ assetId, clientId: Number(clientId), file: fileData.file, duration: fileData.duration }));

      setFileData(undefined);

      if(isMultipartUpload){
        setLoadingMultipart(true);
      }else{
        setLoading(true);
      }

      togglePlayer();

      await new Promise<void>((resolve) => {
        setTimeout(() => {
          togglePlayer();
          setLoading(false);
          setLoadingMultipart(false);
          resolve();
        }, 5000);
      });

      onClose();
    } catch {
      //
    }
  }, [fileData?.file, replaceAssetInParts, assetId, clientId, replaceAsset, togglePlayer, onClose]);

  const clear = (): void => {
    setAlert(undefined);
  };

  useEffect((): void => {
    if (error) {
      setAlert({
        message: error.message,
        clear
      });
    }else if ( multipartError) {
      setAlert({
        message: multipartError.message,
        clear
      });
    }
  }, [error, multipartError]);

  const onFileChange = useCallback((files: File[]) => {
    if (files.length) {
      alert?.clear();

      // Read the file and extract video duration
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        setFileData({ file: files[0], duration: video.duration });
      };

      video.onerror = () => {
      };

      video.src = URL.createObjectURL(files[0]);
    }
  }, [alert]);

  return (
    <Modal
        size='lg'
        show={show}
        backdrop={(uploading || loading || multipartUploading || loadingMultipart) ? 'static' : undefined}
        onHide={onClose}
        contentClassName='bg-dusk text-white'
        className='mx-auto'
      >
        <Modal.Header closeButton>
          <i className="font-size-40px far fa-file-alt" />
          <h1>Replace Event Asset</h1>
        </Modal.Header>
        <Modal.Body>
          <Form.Alert className='mx-32px' show={!!alert} onClose={alert?.clear} variant={alert?.variant}>
            {alert?.message}
          </Form.Alert>
            <p className="text-muted text-center">Supported File Formats: MP4</p>
            <Form className="d-flex flex-column gap-12px m-32px bg-white5 rounded-lg">
              <div className='text-center'>
                {(uploading || loading) ? (
                  <>
                    <div className='text-primary'>
                      <i className='fad fa-spin fa-spinner-third fa-2x' />
                    </div>
                    <p className='font-size-28px text-white m-0'>{progress.toFixed(0)}%</p>
                    {!loading ? (<p className='text-muted'>Uploading Video...</p>
                    ) : (
                      <p className="text-muted">Loading Video...</p>
                    )}
                    <ProgressBar animated now={progress} />
                    <p className='text-muted mt-16px'>{fileData?.file?.name}</p>
                  </>
                ) : (multipartUploading || loadingMultipart) ? (
                  <>
                    <div className='text-primary'>
                      <i className='fad fa-spin fa-spinner-third fa-2x' />
                    </div>
                    <p className='font-size-28px text-white m-0'>{multipartProgress.toFixed(0)}%</p>
                    {!loadingMultipart ? (<p className='text-muted'>Uploading Video...</p>
                    ) : (
                      <p className="text-muted">Loading Video...</p>
                    )}
                    <ProgressBar animated now={multipartProgress} />
                    <p className='text-muted mt-16px'>{fileData?.file?.name}</p>
                  </>
                ) : (
                  <FileDrop
                    className='simulated-live-upload text-muted p-64px'
                    onDrop={onFileChange}
                  >
                    <div className="d-flex flex-column align-items-center gap-8px">
                      <svg width="64" height="64" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.46128 1.07827C9.34286 0.948767 9.17552 0.875 9.00002 0.875C8.82452 0.875 8.65719 0.948767 8.53877 1.07827L5.20542 4.7241C4.97251 4.97885 4.99021 5.37418 5.24496 5.6071C5.49972 5.84002 5.89504 5.82232 6.12796 5.56757L8.37502 3.10983V12.3333C8.37502 12.6785 8.65486 12.9583 9.00002 12.9583C9.34519 12.9583 9.62502 12.6785 9.62502 12.3333V3.10983L11.8721 5.56757C12.105 5.82232 12.5004 5.84002 12.7551 5.6071C13.0099 5.37418 13.0275 4.97885 12.7946 4.7241L9.46128 1.07827Z" fill="currentColor"/>
                        <path d="M2.125 11.5C2.125 11.1548 1.84518 10.875 1.5 10.875C1.15483 10.875 0.875 11.1548 0.875 11.5V11.5458C0.874984 12.6854 0.874967 13.604 0.9721 14.3265C1.07295 15.0766 1.28869 15.7081 1.79029 16.2097C2.29189 16.7113 2.92345 16.9271 3.67354 17.0279C4.39602 17.125 5.31462 17.125 6.45428 17.125H11.5458C12.6854 17.125 13.604 17.125 14.3265 17.0279C15.0766 16.9271 15.7081 16.7113 16.2097 16.2097C16.7113 15.7081 16.9271 15.0766 17.0279 14.3265C17.125 13.604 17.125 12.6854 17.125 11.5458V11.5C17.125 11.1548 16.8452 10.875 16.5 10.875C16.1548 10.875 15.875 11.1548 15.875 11.5C15.875 12.6962 15.8737 13.5304 15.7891 14.1599C15.7068 14.7714 15.5565 15.0952 15.3258 15.3258C15.0952 15.5565 14.7714 15.7068 14.1599 15.7891C13.5304 15.8737 12.6962 15.875 11.5 15.875H6.5C5.30383 15.875 4.46956 15.8737 3.8401 15.7891C3.22863 15.7068 2.90481 15.5565 2.67418 15.3258C2.44354 15.0952 2.29317 14.7714 2.21096 14.1599C2.12633 13.5304 2.125 12.6962 2.125 11.5Z" fill="currentColor"/>
                      </svg>
                      {trimmedName}
                      {Nix.isNil(fileData?.file) ? 'Upload your file' : ' Choose a different file'}
                    </div>
                  </FileDrop>
                )}
              </div>
            </Form>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Submit onClick={upload}>
            Replace Asset
          </Modal.Submit>
          <Modal.Button variant='outline-lightgrey' onClick={cancel}>
            Cancel
          </Modal.Button>
        </Modal.Footer>
      </Modal>
  );
};
